import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { faUserFriends } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { axiosErr } from "@hilos/types/axios";
import HeaderNavigation from "src/components/HeaderNavigation";
import { API_ROUTES, buildAPIRoute, buildRoute } from "src/router/router";
import * as meta from "../user/Meta";
import RoleForm from "./RoleForm";

export default function RoleCreate() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const getInitialData = () => {
    const permissions = {};
    Object.getOwnPropertyNames(meta.PERMISSIONS).forEach(
      (permName) => (permissions[permName] = false)
    );
    return {
      permissions: permissions,
      name: "",
      can_be_deleted: true,
    };
  };

  const formSubmit = async (
    formData,
    setSubmitting,
    setBackendValidationErrors,
    setBackendError
  ) => {
    setBackendValidationErrors({});
    setBackendError("");

    const nextPermissions = Object.getOwnPropertyNames(
      formData.permissions
    ).filter((permName) => formData.permissions[permName]);

    try {
      await axios.post(
        buildAPIRoute(API_ROUTES.ROLE_LIST, {
          ":id": params.id,
        }),
        {
          permissions: nextPermissions,
          name: formData.name,
        }
      );
      setSubmitting(false);
      setSuccess(true);
      setTimeout(() => {
        navigate(buildRoute("account-members"));
      }, 1000 * 2);
    } catch (err) {
      const errorAxios: axiosErr = err as axiosErr;
      setSuccess(false);
      if (errorAxios?.response?.status === 400) {
        console.log("error", errorAxios);
        setBackendValidationErrors(errorAxios.response.data);
      } else {
        setBackendError(
          t(
            "settings:role.error-creating-role",
            "An error occurred while creating your role. Please try again."
          )
        );
      }
      setTimeout(() => {
        setSubmitted(false);
      }, 1000 * 2);
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="h-screen overflow-y-auto bg-gray-50">
      <div className="py-4 px-4 sm:border-b sm:border-gray-200 sm:px-6 lg:px-8">
        <HeaderNavigation
          navPages={[
            {
              name: t("home"),
              url: buildRoute("dashboard"),
            },
            {
              name: t("settings"),
              url: buildRoute("config-account"),
            },
            {
              name: t("roles", "Roles"),
              url: buildRoute("account-members"),
            },
            {
              name: t("settings:role.create-button", "New role"),
              url: buildRoute("role-create"),
            },
          ]}
        />
        <div className="lg:flex lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="mt-2 flex items-center text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
              <FontAwesomeIcon
                //@ts-ignore
                icon={faUserFriends}
                className="mr-4 h-7 w-7"
                aria-hidden="true"
              />
              {t("settings:role.create-button", "New role")}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {t(
                "settings:role.create-subtitle",
                "Use roles to set permissions for users."
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mt-6 max-w-3xl">
          <RoleForm
            data={getInitialData()}
            formSubmit={formSubmit}
            success={success}
            submitted={submitted}
          />
        </div>
      </div>
    </div>
  );
}
